/**
 * Configuration des types de prestataires
 * 
 * Ce fichier centralise les constantes et helpers pour gérer les différents types
 * de prestataires sur la plateforme.
 */

export const PROVIDER_TYPES = {
  HAIR: 'hair',
  NAILS: 'nails',
  MAKEUP: 'makeup',
  EYELASH: 'eyelash',
  VENDOR: 'vendor'
};

export const PROVIDER_TYPE_LABELS = {
  [PROVIDER_TYPES.HAIR]: {
    fr: 'Coiffeuse',
    en: 'Hairstylist',
    de: 'Friseurin',
    nl: 'Kapster',
    singular: { fr: 'coiffeuse', en: 'hairstylist', de: 'Friseurin', nl: 'kapster' },
    plural: { fr: 'coiffeuses', en: 'hairstylists', de: 'Friseurinnen', nl: 'kapsters' },
  },
  [PROVIDER_TYPES.NAILS]: {
    fr: 'Prestataire Ongles',
    en: 'Nail Artist',
    de: 'Nageldesignerin',
    nl: 'Nagelstyliste',
    singular: { fr: 'prestataire ongles', en: 'nail artist', de: 'Nageldesignerin', nl: 'nagelstyliste' },
    plural: { fr: 'prestataires ongles', en: 'nail artists', de: 'Nageldesignerinnen', nl: 'nagelstylistes' },
  },
  [PROVIDER_TYPES.MAKEUP]: {
    fr: 'MUA',
    en: 'Makeup Artist',
    de: 'Make-up-Artistin',
    nl: 'Make-up Artiest',
    singular: { fr: 'MUA', en: 'makeup artist', de: 'Make-up-Artistin', nl: 'make-up artiest' },
    plural: { fr: 'MUAs', en: 'makeup artists', de: 'Make-up-Artistinnen', nl: 'make-up artiesten' },
  },
  [PROVIDER_TYPES.EYELASH]: {
    fr: 'Prestataire Cils',
    en: 'Eyelash Artist',
    de: 'Wimpernstylistin',
    nl: 'Wimperstyliste',
    singular: { fr: 'prestataire cils', en: 'eyelash artist', de: 'Wimpernstylistin', nl: 'wimperstyliste' },
    plural: { fr: 'prestataires cils', en: 'eyelash artists', de: 'Wimpernstylistinnen', nl: 'wimperstylistes' },
  },
  [PROVIDER_TYPES.VENDOR]: {
    fr: 'Vente',
    en: 'Vendor',
    de: 'Verkauf',
    nl: 'Verkoop',
    singular: { fr: 'vendeur', en: 'vendor', de: 'Verkäufer', nl: 'verkoper' },
    plural: { fr: 'vendeurs', en: 'vendors', de: 'Verkäufer', nl: 'verkopers' },
  },
};

export const DEFAULT_PROVIDER_TYPE = PROVIDER_TYPES.HAIR;

const LANG_KEYS = ['fr', 'en', 'de', 'nl'];

/**
 * Obtient le label d'un type de prestataire (singulier ou pluriel selon la langue).
 * @param {string} type - Le type de prestataire (hair, nails, makeup, eyelash)
 * @param {string} lang - La langue ('fr', 'en', 'de', 'nl')
 * @param {object} options - { plural: boolean } pour forcer le pluriel (défaut: selon count)
 * @param {number} count - Si fourni, retourne le pluriel quand count !== 1
 * @returns {string} Le label traduit
 */
export function getProviderTypeLabel(type, lang = 'fr', options = {}, count = 1) {
  const labels = PROVIDER_TYPE_LABELS[type];
  if (!labels) return type;
  const langKey = LANG_KEYS.includes(lang) ? lang : 'fr';
  const usePlural = options.plural ?? (count !== 1);
  const plural = typeof labels.plural === 'object' ? labels.plural[langKey] : labels.plural;
  const singular = typeof labels.singular === 'object' ? labels.singular[langKey] : labels.singular;
  return usePlural ? (plural || labels[langKey]) : (singular || labels[langKey]);
}

/**
 * Vérifie si un type de prestataire est valide
 * @param {string} type - Le type à vérifier
 * @returns {boolean}
 */
export function isValidProviderType(type) {
  return Object.values(PROVIDER_TYPES).includes(type);
}
